'use client';

import { cn } from '../lib/utils';
import { Trans } from '../makerkit/trans';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  variant?: 'primary' | 'muted' | 'white';
}

const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-8 w-8 border-[3px]',
  xl: 'h-12 w-12 border-[3px]',
};

const variantClasses = {
  primary: 'border-primary border-t-transparent',
  muted: 'border-muted-foreground/20 border-t-muted-foreground',
  white: 'border-white/30 border-t-white',
};

export function Spinner({
  size = 'md',
  className,
  variant = 'primary',
}: SpinnerProps) {
  return (
    <div
      className={cn(
        'inline-block animate-spin rounded-full',
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">
        <Trans i18nKey="common:loading" />
      </span>
    </div>
  );
}

/*
 * ===========================================================================
 * 🔴 `LoadingOverlay` UND `LoadingBar` SIND AM 20.09.2026 HIER ENTFALLEN
 * ===========================================================================
 *
 * Bauteil-Inventur, Befund 6: „Zwei `LoadingOverlay` gleichen Namens, beide
 * exportiert, verschiedene Gestalt — `spinner.tsx:48`
 * (`fixed inset-0 z-50 backdrop-blur`) gegen `loading-overlay.tsx`
 * (`fullPage`, `h-screen w-screen z-100`). 18 Renderstellen in 12 Dateien;
 * **welche Datei welches importiert, ist nicht gemessen.**"
 *
 * 🟢 JETZT GEMESSEN, und das entscheidet den Fall. Ueber den ganzen Baum
 * (`.ts`/`.tsx`, ohne `node_modules`/`dist`/`.next`), nach dem NAMEN in der
 * Importliste und nach dem MODUL ausgewertet — nicht nach dem Wort im Text:
 *
 *     LoadingOverlay aus `@kit/ui/spinner`          →  0
 *     LoadingOverlay aus `@kit/ui/loading-overlay`  → 14
 *     LoadingBar (aus irgendeinem Modul)            →  0
 *
 * Es gab also keine zwei Gestalten im Gebrauch, sondern eine gebrauchte und
 * eine tote, die den Namen besetzt hielt. Wer `LoadingOverlay` tippte und die
 * Selbstvervollstaendigung `@kit/ui/spinner` anbot, bekam die andere Fassung —
 * das war der Schaden, nicht das Bild auf dem Schirm.
 *
 * ⚠️ DER GEBRAUCHTE BLEIBT, UND ZWAR UNVERAENDERT: `makerkit/loading-overlay.tsx`
 * (`@kit/ui/loading-overlay`). An dieser Aenderung rendert keine Flaeche anders
 * — sie entfernt ausschliesslich nicht aufgerufenen Code.
 *
 * ⚠️ P4 bleibt davon unberuehrt: „Platzhalter in Zeilenform, nicht ein Kreisel
 * in der Mitte" gilt fuer BEIDE Fassungen, und dass ein Ueberlagerungs-Kreisel
 * die falsche Bauform fuer eine ladende Flaeche ist, ist Befund 1 der Inventur
 * und nicht dieser hier.
 *
 * Vorbild fuer die Form dieses Grabsteins: `LoadingInline`, am 24.08.2026 aus
 * derselben Datei und aus demselben Grund entfallen — siehe unten.
 */

/*
 * 🔴 `LoadingInline` ist am 24.08.2026 entfallen: NULL Aufrufstellen — und ein
 * einprogrammiertes „Loading..." als Vorgabewert, also eine nutzerseitige
 * Zeichenkette ohne Uebersetzungsschluessel in einem GETEILTEN Bauteil.
 *
 * Was Blatt P4 an dieser Stelle verlangt, steht ohnehin anders da: „Knoepfe
 * werden nicht ersetzt, sondern gesperrt … Aufschrift bleibt, ein kleiner
 * Kreisel tritt NEBEN die Aufschrift." Das machen die Knoepfe selbst, mit ihrem
 * eigenen Text — ein Bauteil mit eigener Beschriftung waere dort der falsche
 * Weg.
 */
